// M13.1 — 100% normalised stacked area chart, used inside SourceMixSection.
//
// Renders React SVG directly using d3-shape's stack() generator. Top 8
// channels by total sessions over the window become individual stacked
// bands; everything else collapses into a single grey "Other" band so
// the chart stays legible. The shared PDF builder (source-mix-svg.ts in
// the next commit) mirrors the same shape for the static export.
//
// Design constraints:
// - Bands are sorted by descending total so the largest channel is at
//   the bottom (least visual movement under the legend).
// - The Y axis is fixed at 0..100% (shares always sum to 100).
// - X-axis ticks honour the page-level granularity.
// - Tooltips show every visible channel's percentage at the hovered
//   date, plus the absolute session count for that channel.

import { useEffect, useMemo, useRef, useState } from "react";
import { bisector, max, sum } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack, stackOffsetExpand, type SeriesPoint } from "d3-shape";
import type { ReportSourceMixTrendRow } from "@/lib/api";
import { channelColor, OTHER_CHANNEL_COLOR } from "./channel-colors";
import type { Granularity } from "./granularity";

const TOP_BAND_LIMIT = 8;
const OTHER_LABEL = "Other";

const DEFAULT_HEIGHT = 220;
const MARGIN = { top: 20, right: 16, bottom: 28, left: 44 };
const AXIS_COLOR = "#6B7280"; // neutral-500
const GRID_COLOR = "#E5E7EB"; // neutral-200

export interface SourceMixChartProps {
  /** Pre-aggregated per-(date, channel) trend rows from the payload. */
  rows: ReportSourceMixTrendRow[];
  granularity: Granularity;
  /** Used purely for the aria-label so the two charts (current /
   *  compare) read distinguishably to screen readers. */
  ariaLabel: string;
}

export function SourceMixChart({ rows, granularity, ariaLabel }: SourceMixChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(Math.max(320, Math.floor(w)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = DEFAULT_HEIGHT;
  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = height - MARGIN.top - MARGIN.bottom;

  // ---- bucket the long tail into "Other" ---------------------------------
  // Sort channels by total sessions over the window; keep the top
  // TOP_BAND_LIMIT and roll the rest into a single "Other" series.
  // `byDate` is a per-date matrix where `cells[k]` is the session count
  // for channel k on that day. `date` is kept on the row so d3-shape
  // tooltips/area generators can resolve back to the timestamp without a
  // sidecar lookup.
  const { keys, byDate, totalsByChannel } = useMemo(() => {
    const totals = new Map<string, number>();
    const dateSet = new Set<string>();
    for (const r of rows) {
      totals.set(r.channel, (totals.get(r.channel) ?? 0) + r.sessions);
      dateSet.add(r.date);
    }
    const sortedChannels = Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([ch]) => ch);
    const topChannels = sortedChannels.slice(0, TOP_BAND_LIMIT);
    const otherChannels = new Set(sortedChannels.slice(TOP_BAND_LIMIT));
    const includeOther = otherChannels.size > 0;

    const dates = Array.from(dateSet).sort();
    const keyList = includeOther ? [...topChannels, OTHER_LABEL] : topChannels;

    interface StackRow {
      date: string;
      cells: Record<string, number>;
    }
    const byDate: StackRow[] = dates.map((d) => {
      const cells: Record<string, number> = {};
      for (const k of keyList) cells[k] = 0;
      return { date: d, cells };
    });
    const indexByDate = new Map(byDate.map((r, i) => [r.date, i]));
    for (const r of rows) {
      const idx = indexByDate.get(r.date);
      if (idx === undefined) continue;
      const key = otherChannels.has(r.channel) ? OTHER_LABEL : r.channel;
      byDate[idx].cells[key] = (byDate[idx].cells[key] ?? 0) + r.sessions;
    }
    return { keys: keyList, byDate, totalsByChannel: totals };
  }, [rows]);

  // d3-stack works on a flattened cells row (it keys into the row by the
  // channel name), so we project byDate down to `{ ...cells }` for the
  // generator while keeping the parent `date` in a parallel array.
  const flatCells = useMemo(() => byDate.map((r) => ({ ...r.cells })), [byDate]);

  const { stacked, xScale, yScale, xTicks } = useMemo(() => {
    if (byDate.length === 0) {
      return {
        stacked: [],
        xScale: scaleTime().range([0, innerW]),
        yScale: scaleLinear().range([innerH, 0]),
        xTicks: [] as Date[],
      };
    }
    const stackGen = d3Stack<Record<string, number>>().keys(keys).offset(stackOffsetExpand);
    const stacked = stackGen(flatCells);
    const dates = byDate.map((d) => new Date(`${d.date}T00:00:00Z`));
    const [xMin, xMax] = [dates[0], dates[dates.length - 1]];
    // d3-shape with stackOffsetExpand normalises to 0..1 already, so the
    // Y axis stays identity 0..1 (rendered as 0..100% via tick labels).
    const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
    const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
    const yScale = scaleLinear().domain([0, yMaxRaw]).range([innerH, 0]);
    const xTickCount = granularity === "day" ? 6 : granularity === "week" ? 5 : 4;
    return { stacked, xScale, yScale, xTicks: xScale.ticks(xTickCount) };
  }, [byDate, flatCells, keys, innerW, innerH, granularity]);

  const areaGen = useMemo(() => {
    return d3Area<SeriesPoint<Record<string, number>>>()
      .x((_, i) => (byDate[i] ? xScale(new Date(`${byDate[i].date}T00:00:00Z`)) : 0))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]));
  }, [xScale, yScale, byDate]);

  const yTickPercents = [0, 25, 50, 75, 100];

  // ---- hover --------------------------------------------------------------
  const dateObjs = useMemo(
    () => byDate.map((d) => ({ date: d.date, dateObj: new Date(`${d.date}T00:00:00Z`) })),
    [byDate],
  );
  const bisectDate = useMemo(
    () => bisector<{ date: string; dateObj: Date }, Date>((d) => d.dateObj).center,
    [],
  );

  const onMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xInside = event.clientX - rect.left - MARGIN.left;
    if (xInside < 0 || xInside > innerW) {
      setHoverX(null);
      return;
    }
    setHoverX(xInside);
  };

  const hoverInfo = useMemo(() => {
    if (hoverX === null || dateObjs.length === 0) return null;
    const dateAtX = xScale.invert(hoverX);
    const idx = bisectDate(dateObjs, dateAtX);
    const ref = dateObjs[Math.max(0, Math.min(dateObjs.length - 1, idx))];
    const cellRow = byDate.find((r) => r.date === ref.date);
    if (!cellRow) return null;
    const cx = xScale(ref.dateObj);
    const total = sum(keys, (k) => cellRow.cells[k] ?? 0);
    const series = keys.map((k) => {
      const v = cellRow.cells[k] ?? 0;
      return { label: k, value: v, share: total > 0 ? v / total : 0 };
    });
    return { cx, date: ref.date, series, total };
  }, [hoverX, dateObjs, xScale, bisectDate, byDate, keys]);

  if (rows.length === 0 || byDate.length === 0) {
    return (
      <div
        ref={containerRef}
        className="flex h-56 items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-500"
      >
        No traffic in the selected window.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        className="ts-chart-reveal font-sans"
      >
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {/* Y gridlines + tick labels (0%..100%) */}
          {yTickPercents.map((pct) => {
            const y = yScale(pct / 100);
            return (
              <g key={`y-${pct}`}>
                <line x1={0} y1={y} x2={innerW} y2={y} stroke={GRID_COLOR} />
                <text x={-8} y={y + 3} fontSize={10} fill={AXIS_COLOR} textAnchor="end">
                  {pct}%
                </text>
              </g>
            );
          })}
          {/* X ticks */}
          {xTicks.map((d, i) => (
            <g key={`x-${i}`}>
              <line x1={xScale(d)} y1={innerH} x2={xScale(d)} y2={innerH + 4} stroke={AXIS_COLOR} />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill={AXIS_COLOR}
                textAnchor="middle"
              >
                {formatXTick(d, granularity)}
              </text>
            </g>
          ))}
          {/* Stacked bands — drawn from bottom (largest) up so the
              biggest channel is the visual base. */}
          {stacked.map((s) => {
            const d = areaGen(s);
            if (!d) return null;
            const fill = s.key === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(s.key);
            return (
              <path
                key={s.key}
                d={d}
                fill={fill}
                stroke="#ffffff"
                strokeWidth={0.5}
                opacity={0.92}
              />
            );
          })}
          {/* Hover crosshair */}
          {hoverInfo ? (
            <line
              x1={hoverInfo.cx}
              y1={0}
              x2={hoverInfo.cx}
              y2={innerH}
              stroke={AXIS_COLOR}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          ) : null}
          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHoverX(null)}
          />
        </g>
      </svg>

      {/* Legend — chips for every visible band, including "Other". */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {keys.map((k) => {
          const total = totalsByChannel.get(k) ?? 0;
          return (
            <span key={k} className="inline-flex items-center gap-1.5 text-xs text-neutral-700">
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{
                  backgroundColor: k === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(k),
                }}
              />
              <span className="font-semibold text-neutral-950">{k}</span>
              {k !== OTHER_LABEL ? (
                <span className="tabular-nums text-neutral-500">{compactNumber(total)}</span>
              ) : null}
            </span>
          );
        })}
      </div>

      {hoverInfo ? (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm"
          style={{
            left: `${hoverInfo.cx + MARGIN.left}px`,
            top: `${MARGIN.top}px`,
            transform: `translate(-50%, -110%)`,
          }}
        >
          <p className="font-semibold text-neutral-950">{formatTooltipDate(hoverInfo.date)}</p>
          <ul className="mt-1 space-y-0.5">
            {hoverInfo.series
              .filter((s) => s.value > 0)
              .sort((a, b) => b.value - a.value)
              .map((s) => (
                <li key={s.label} className="flex items-center gap-2 whitespace-nowrap">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor:
                        s.label === OTHER_LABEL ? OTHER_CHANNEL_COLOR : channelColor(s.label),
                    }}
                    aria-hidden="true"
                  />
                  <span className="text-neutral-600">{s.label}</span>
                  <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                    {(s.share * 100).toFixed(1)}%
                  </span>
                  <span className="ml-1 text-neutral-500 tabular-nums">
                    ({compactNumber(s.value)})
                  </span>
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatXTick(d: Date, granularity: Granularity): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatTooltipDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

function compactNumber(n: number): string {
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 10_000) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  }
  return new Intl.NumberFormat("en").format(Math.round(n));
}
